import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Column,
  Copy,
  Accordion,
  AccordionExpandAll,
  useAccordion,
  useMediaQueries,
} from '@honda-canada/design-system-react';
import themeStyles from './Accordions.styles';
import {
  getAccordionItems,
  ACCORDION_TITLE_OFFSET_DESKTOP_HEIGHT,
  ACCORDION_TITLE_OFFSET_TABLET_HEIGHT,
  ACCORDION_TITLE_OFFSET_MOBILE_HEIGHT,
} from './utils';
import Accessories from '../Accessories';
import AdditionalWarranty from '../AdditionalWarranty';
import PaymentSummary from '../PaymentSummary';

const ExpandCollapseLabel = themeStyles.apply(Copy, 'ExpandCollapseLabel');
const AccordionWrapper = themeStyles.apply(Column, 'AccordionWrapper');

const Accordions = ({ selectedTrimId }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useMediaQueries();

  // accordion offset in dsr needs a single value, not an array of values
  let titleOffset = ACCORDION_TITLE_OFFSET_DESKTOP_HEIGHT;
  if (isMobile) {
    titleOffset = ACCORDION_TITLE_OFFSET_MOBILE_HEIGHT;
  }
  if (isTablet) {
    titleOffset = ACCORDION_TITLE_OFFSET_TABLET_HEIGHT;
  }

  const AccessoriesComponent = <Accessories selectedTrimId={selectedTrimId} />;

  const WarrantyComponent = <AdditionalWarranty selectedTrimId={selectedTrimId} />;

  const PaymentComponent = <PaymentSummary />;

  const accordionItemLabels = {
    accessoriesLabel: t('Sub-apps.PriceAndFinance.accessoriesLabel'),
    additionalWarrantyLabel: t('Sub-apps.PriceAndFinance.additionalWarrantyLabel'),
    paymentSummaryLabel: t('Sub-apps.PriceAndFinance.paymentSummaryLabel'),
  };

  const expandAllLabel = t('Sub-apps.PriceAndFinance.expandAllLabel');
  const collapseAllLabel = t('Sub-apps.PriceAndFinance.collapseAllLabel');

  const items = getAccordionItems(AccessoriesComponent, WarrantyComponent, PaymentComponent, accordionItemLabels);

  const behaviour = useAccordion(items);
  const { toggleAllItems, isAllExpanded } = behaviour;

  const expandCollapseText = isAllExpanded ? collapseAllLabel : expandAllLabel;

  useEffect(() => {
    toggleAllItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <AccordionExpandAll
        justifyContent="flex-end"
        iconColorOverride="primary"
        toggleItem={toggleAllItems}
        isActive={isAllExpanded}
        py={['xs', 'xs', 'default']}
        px={['0', '0', '0']}
        width="100%"
        data-testid="cy-accordion-expand-all"
      >
        <ExpandCollapseLabel>{expandCollapseText}</ExpandCollapseLabel>
      </AccordionExpandAll>
      <AccordionWrapper>
        <Accordion items={items} behaviour={behaviour} isTitleSticky titleOffset={titleOffset} />
      </AccordionWrapper>
    </>
  );
};

Accordions.propTypes = {
  selectedTrimId: PropTypes.string,
};

export default Accordions;
